/**
 * Abbreviating large numbers for display — and only for display.
 *
 * A market cap of 3,247,000,000 is unreadable and `3.25B` is not, but the
 * abbreviation is *presentation*, so it happens here rather than on the wire.
 * Sending `"3.25B"` would make the column a column of strings: it would sort
 * lexically (putting `9M` above `1B`), stop being filterable by magnitude, and
 * stop being usable in an expression. The value stays a number all the way to
 * the cell renderer, which is also why the same column can be sorted, filtered
 * and computed with while reading as `3.25B`.
 *
 * A **fixed** unit and an automatic one are both offered because they answer
 * different questions. Every row in millions compares at a glance — that is
 * what a screener column is for — whereas a mix of `900M` and `1.1B` makes the
 * reader do arithmetic. `compact` earns its place on a column whose magnitudes
 * genuinely differ by orders, where a fixed unit gives `0.000004M`.
 *
 * ## Only `compact` follows the market, and that is the point of the split
 *
 * A screener shows one market at a time (D40), so a grid has one locale rather
 * than one per row. `compact` takes it, which is what makes an Indian screen
 * read in lakh and crore instead of K/M/B.
 *
 * The **fixed** units deliberately do not. `millions` is the user having chosen
 * a unit — divide by 1e6 and label it `M` — and a unit that silently became
 * `lakh` on an Indian screen would mean the column no longer says what was asked
 * for. `M` is `M` in every market for the same reason a metre is.
 *
 * That leaves an Indian user wanting crore with `compact`, which gives exactly
 * that under `en-IN`. So the gap closes without adding `lakhs`/`crores` to the
 * contract enum — which would be a schema change, codegen on four platforms,
 * and two more units every other market has to ignore.
 */

import type { ScreenerColumn } from "@qufolio/model";

export type Notation = NonNullable<ScreenerColumn["notation"]>;

/** Divisor and suffix per fixed unit. */
const FIXED: Partial<Record<Notation, { by: number; suffix: string }>> = {
  thousands: { by: 1e3, suffix: "K" },
  millions: { by: 1e6, suffix: "M" },
  billions: { by: 1e9, suffix: "B" },
};

/**
 * Format one value. Returns null when the notation does not apply, so a caller
 * can fall back to its own rendering rather than this inventing one.
 *
 * Non-finite values pass through untouched: `Infinity` from a divide-by-zero is
 * a real result the grid must show and sort, and `NaN` abbreviated is nonsense.
 */
/**
 * What a number shows when nobody said.
 *
 * **Two, and it lives here** because `columns.ts` already imports this module;
 * declaring it there and importing it back would be a cycle. `formatNotation` An abbreviated column
 * has defaulted to two decimals since it was written, so a column with notation
 * read `3.25B` while the identical column without it rendered whatever the raw
 * value happened to carry — `184.22000000000003` among them. One default for one
 * kind of thing.
 *
 * It is a *rendering* default and is deliberately not written into the config.
 * Storing it would put a number nobody chose on every column, make a saved
 * screen differ from its own reload, and take away the one thing absence is good
 * for here: "I have no opinion, use yours".
 */
export const DEFAULT_PRECISION = 2;

export function formatNotation(
  value: unknown,
  notation: Notation | undefined,
  precision?: number,
  /**
   * BCP 47, for `compact` only. `undefined` means the browser's own, which is
   * what every existing caller gets and what a US screen wants.
   */
  locale?: string,
): string | null {
  if (!notation || notation === "plain") return null;
  if (typeof value !== "number" || !Number.isFinite(value)) return null;

  const fixed = FIXED[notation];
  if (fixed) {
    // `precision` still means decimal places — of the scaled number, which is
    // the one being read. Two decimals on billions is the useful default;
    // two decimals on the raw value would be noise nobody sees.
    const digits = precision ?? DEFAULT_PRECISION;
    return `${(value / fixed.by).toFixed(digits)}${fixed.suffix}`;
  }

  // `compact` is Intl's, not ours: it is locale-aware (lakh and crore in
  // en-IN, not just K/M/B), and reimplementing that badly is a worse column
  // than not offering it.
  //
  // The locale is the **screened market's**, not the reader's. A screen of NSE
  // symbols reads in crore whoever is looking at it, because the alternative is
  // an Indian column that says `1.8T` — a wrong-looking number rather than a
  // differently-formatted one.
  return formatterFor(locale, precision ?? DEFAULT_PRECISION).format(value);
}

/**
 * `Intl.NumberFormat` is expensive to construct and this runs per cell, per
 * render, over thousands of rows.
 */
const compactFormatters = new Map<string, Intl.NumberFormat>();

function formatterFor(locale: string | undefined, digits: number): Intl.NumberFormat {
  const key = `${locale ?? ""}|${digits}`;
  let formatter = compactFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      notation: "compact",
      maximumFractionDigits: digits,
    });
    compactFormatters.set(key, formatter);
  }
  return formatter;
}

/**
 * Share and dollar counts read in K / M / B unless the column says otherwise
 * (owner, 2026-10-26): `214,312,889` is a number nobody reads at a glance.
 *
 * By **base field**, so a weekly `volume` gets it too. A named list rather than
 * a `/volume/i` match, because most fields with "volume" in the name are not
 * counts — `zVolume` is a z-score, `volumeOsc` an oscillator, `obvAge` a number
 * of bars — and abbreviating a 1.8 to `1.8` is harmless but a 0.0004 to `0` is
 * not.
 *
 * Like `DEFAULT_PRECISION`, a rendering default and never written into the
 * config. Choosing Plain on one of these stores `"plain"`, which is the one
 * place absence and Plain now differ.
 */
const COMPACT_BY_DEFAULT = new Set([
  "volume",
  "netVolume",
  "recVolume",
  "obv",
  "obvEma",
  "jasObv",
  "jasObvEma",
  "marketCap",
]);

export function defaultNotation(baseField: string): Notation | undefined {
  return COMPACT_BY_DEFAULT.has(baseField) ? "compact" : undefined;
}

/** What a column renders with: its own choice, else the field's default. */
export function effectiveNotation(
  column: ScreenerColumn | undefined,
  baseField: string,
): Notation | undefined {
  return column?.notation ?? defaultNotation(baseField);
}

/** True when this column wants abbreviating at all. */
export const hasNotation = (column: ScreenerColumn | undefined): boolean =>
  !!column?.notation && column.notation !== "plain";

/** The choices offered in the editor, in increasing magnitude. */
export const NOTATIONS: { id: Notation; label: string }[] = [
  { id: "plain", label: "Plain" },
  { id: "compact", label: "Auto — K / M / B" },
  { id: "thousands", label: "Thousands (K)" },
  { id: "millions", label: "Millions (M)" },
  { id: "billions", label: "Billions (B)" },
];
